import { useEffect, useMemo, useSyncExternalStore } from 'react';

import { I18nContext } from './context';
import { englishMessages, turkishMessages } from './messages';

import type {
    I18nContextValue,
    Locale,
    LocalePreference,
    Translate,
    TranslateOptions,
} from './types';
import type { ReactElement, ReactNode } from 'react';

const DEFAULT_LOCALE: Locale = 'en';
const SUPPORTED_LOCALES: readonly Locale[] = ['en', 'tr'];
const LOCALE_QUERY_PARAMETER = 'l';
const localePreferenceListeners = new Set<() => void>();

function isLocale(value: string | null | undefined): value is Locale {
    return value !== null && value !== undefined && SUPPORTED_LOCALES.includes(value as Locale);
}

function subscribeToLocalePreference(onChange: () => void): () => void {
    localePreferenceListeners.add(onChange);
    window.addEventListener('popstate', onChange);
    return () => {
        localePreferenceListeners.delete(onChange);
        window.removeEventListener('popstate', onChange);
    };
}

function readLocalePreference(): LocalePreference {
    const requestedLocale = new URLSearchParams(window.location.search).get(LOCALE_QUERY_PARAMETER);
    return isLocale(requestedLocale) ? requestedLocale : 'auto';
}

function writeLocalePreference(preference: LocalePreference): void {
    const url = new URL(window.location.href);
    if (preference === 'auto') url.searchParams.delete(LOCALE_QUERY_PARAMETER);
    else url.searchParams.set(LOCALE_QUERY_PARAMETER, preference);
    if (url.href === window.location.href) return;
    window.history.replaceState(window.history.state, '', url);
    localePreferenceListeners.forEach((listener) => {
        listener();
    });
}

function subscribeToBrowserLanguage(onChange: () => void): () => void {
    window.addEventListener('languagechange', onChange);
    return () => {
        window.removeEventListener('languagechange', onChange);
    };
}

function browserLocale(): Locale {
    for (const languageTag of navigator.languages) {
        const primarySubtag = languageTag.split('-')[0]?.toLowerCase();
        if (isLocale(primarySubtag)) return primarySubtag;
    }
    return DEFAULT_LOCALE;
}

function interpolate(message: string, options: TranslateOptions): string {
    return message.replace(/%\{([^}]+)\}/g, (match, parameter: string) => {
        const value = options[parameter];
        return value === undefined ? match : String(value);
    });
}

export function I18nProvider({ children }: { children: ReactNode }): ReactElement {
    const localePreference = useSyncExternalStore(
        subscribeToLocalePreference,
        readLocalePreference
    );
    const detectedLocale = useSyncExternalStore(subscribeToBrowserLanguage, browserLocale);
    const locale = localePreference === 'auto' ? detectedLocale : localePreference;

    useEffect(() => {
        writeLocalePreference(localePreference);
    }, [localePreference]);
    useEffect(() => {
        document.documentElement.lang = locale;
    }, [locale]);

    const value = useMemo<I18nContextValue>(() => {
        const messages = locale === 'tr' ? turkishMessages : englishMessages;
        const translate: Translate = (key, options = {}) => interpolate(messages[key], options);
        return { locale, localePreference, setLocalePreference: writeLocalePreference, translate };
    }, [locale, localePreference]);

    return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}
